<template>
  <div v-if="!isMinimized">
    <div class="modal-backdrop fade show" @click="closeAllTabs"></div>

    <div class="ticket-tabs-container">
      <!-- Top tabs bar -->
      <TicketTabsBar
        :tickets="openedTickets"
        :active-tab-index="activeTabIndex"
        :if-prevopen="ifPrevopen"
        :if-nextopen="ifNextopen"
        @switch-tab="switchTab"
        @close-tab="closeTab"
        @open-search="showTicketSearch = true"
        @prev-open="prevOpen"
        @next-open="nextOpen"
        @minimize="minimizeWindow"
        @close-all="closeAllTabs"
      />

      <!-- Main content area -->
      <div class="main-content-area">
        <!-- Left sidebar - Active ticket card -->
        <LeftSidebar
          :ticket="activeTicket"
          :expanded-sections="{
            about: expandedSections.about,
            clients: expandedSections.clients,
          }"
          @update-sections="updateSection"
          @add-assigned-user="handleAddAssignedUsers"
          @remove-assigned-user="handleRemoveAssignedUser"
        />

        <!-- Center content area -->
        <div class="center-content">
          <div v-if="activeTicket" class="content-wrapper">
            <!-- Content tabs -->
            <div class="content-tabs">
              <b-tabs content-class="mt-3" nav-class="custom-tabs">
                <b-tab title="Головне" active>
                  <MainTab :ticket="activeTicket" />
                </b-tab>
                <b-tab title="Чат">
                  <ChatTab :ticket="activeTicket" />
                </b-tab>
                <b-tab title="Клієнти">
                  <ClientsTab :ticket="activeTicket" />
                </b-tab>
                <b-tab title="Пристрої">
                  <DevicesTab :ticket="activeTicket" />
                </b-tab>
                <b-tab title="Файли">
                  <FilesTab :ticket="activeTicket" :obj="activeTicket" />
                </b-tab>
                <b-tab title="Історія">
                  <HistoryTab :ticket="activeTicket" />
                </b-tab>
                <b-tab title="Таймлайн">
                  <TimelineTab :ticket="activeTicket" />
                </b-tab>
              </b-tabs>
            </div>
          </div>
        </div>

        <!-- Right sidebar with action sections -->
        <RightSidebar
          :ticket="activeTicket"
          :expanded-sections="{
            closed: expandedSections.closed,
            preopened: expandedSections.preopened,
            solution: expandedSections.solution,
            reopen: expandedSections.reopen,
          }"
          @update-sections="updateSection"
          @show-solution-details="showSolutionDetails = true"
        />
      </div>

      <!-- Footer -->
      <div class="content-footer">
        <button v-if="perms[301]" @click="editTicket" class="btn btn-dark">
          edit
        </button>
      </div>
    </div>

    <!-- Solution details modal -->
    <SolutionDetailsModal
      v-if="activeTicket"
      :show="showSolutionDetails"
      :ticket="activeTicket"
      @close="showSolutionDetails = false"
    />

    <!-- Ticket search modal -->
    <TicketSearchModal
      :show="showTicketSearch"
      :tickets="filteredTickets"
      :search-query="searchQuery"
      @close="closeTicketSearch"
      @search="searchTickets"
      @open-ticket="openTicketFromSearch"
    />
  </div>
</template>

<script>
import { storeS } from "@/store";
import { useTicketWindows } from "@/composables/useTicketWindows.js";
import { Tickets } from "@/API";
import MainTab from "./tabs/Main/MainTab.vue";
import ChatTab from "./tabs/Chat/ChatTab.vue";
import ClientsTab from "./tabs/Clients/ClientsTab.vue";
import TasksTab from "./tabs/Tasks/TasksTab.vue";
import DevicesTab from "./tabs/Devices/DevicesTab.vue";
import FilesTab from "./tabs/Files/FilesTab.vue";
import HistoryTab from "./tabs/History/HistoryTab.vue";
import TimelineTab from "./tabs/Timeline/TimelineTab.vue";
import TicketTabsBar from "./components/TicketTabsBar.vue";
import LeftSidebar from "./components/LeftSidebar.vue";
import RightSidebar from "./components/RightSidebar.vue";
import TicketSearchModal from "./components/TicketSearchModal.vue";
import SolutionDetailsModal from "./components/SolutionDetailsModal.vue";

let apiServe = new Tickets();

export default {
  name: "ViewBoxNew",
  components: {
    MainTab,
    ChatTab,
    ClientsTab,
    TasksTab,
    DevicesTab,
    FilesTab,
    HistoryTab,
    TimelineTab,
    TicketTabsBar,
    LeftSidebar,
    RightSidebar,
    TicketSearchModal,
    SolutionDetailsModal,
  },
  props: {
    windowId: {
      type: String,
      required: true,
    },
    ifPrevopen: {
      type: Boolean,
      default: false,
    },
    ifNextopen: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const ticketWindowsManager = useTicketWindows();
    return {
      ticketWindowsManager,
    };
  },
  data() {
    return {
      expandedSections: {
        about: true,
        clients: false,
        closed: false,
        preopened: false,
        solution: false,
        reopen: false,
      },
      showTicketSearch: false,
      showSolutionDetails: false,
      searchQuery: "",
      filteredTickets: [],
      allTickets: [],
    };
  },
  computed: {
    perms() {
      return storeS.perms;
    },
    currentWindow() {
      return this.ticketWindowsManager.windows.value.find(
        (w) => w.id === this.windowId
      );
    },
    openedTickets() {
      return this.currentWindow?.tickets || [];
    },
    activeTabIndex() {
      return this.currentWindow?.activeTabIndex || 0;
    },
    activeTicket() {
      return this.openedTickets[this.activeTabIndex] || null;
    },
    isMinimized() {
      return this.currentWindow?.isMinimized || false;
    },
  },
  methods: {
    switchTab(index) {
      this.ticketWindowsManager.setActiveTab(this.windowId, index);
    },
    closeTab(index) {
      this.ticketWindowsManager.closeTicketTab(this.windowId, index);
      // Оновити таблицю тікетів після закриття (могли бути внесені зміни)
      this.eventBus.emit("saveTickets", true);
    },
    closeAllTabs() {
      this.ticketWindowsManager.closeWindow(this.windowId);
      // Оновити таблицю тікетів після закриття (могли бути внесені зміни)
      this.eventBus.emit("saveTickets", true);
    },
    minimizeWindow() {
      this.ticketWindowsManager.minimizeWindow(this.windowId);
    },
    prevOpen() {
      this.$emit("prevOpen", this.activeTicket.ticketId);
    },
    nextOpen() {
      this.$emit("nextOpen", this.activeTicket.ticketId);
    },
    editTicket() {
      this.$emit("edit", this.activeTicket);
    },
    updateSection({ section, value }) {
      this.expandedSections[section] = value;
    },
    closeTicketSearch() {
      this.showTicketSearch = false;
      this.searchQuery = "";
      this.filteredTickets = [];
    },
    searchTickets(query) {
      this.searchQuery = query;

      if (!query.trim()) {
        this.filteredTickets = [];
        return;
      }

      this.$emit("searchTickets", query, (tickets) => {
        this.filteredTickets = tickets;
      });
    },
    openTicketFromSearch(ticketId) {
      this.closeTicketSearch();
      this.$emit("openTicketById", ticketId);
    },
    handleAddAssignedUsers(selectedWorkers) {
      if (
        !this.activeTicket ||
        !selectedWorkers ||
        selectedWorkers.length === 0
      ) {
        return;
      }

      // Об'єднуємо існуючих працівників з новими
      const existingWorkerIds = (this.activeTicket.assignedWorkers || []).map(
        (w) => w.workerId
      );
      const newWorkerIds = selectedWorkers.map((worker) => worker.workerId);
      const allWorkerIds = [...existingWorkerIds, ...newWorkerIds];

      const obj = {
        workers: allWorkerIds,
      };

      apiServe.addTeammates(this.activeTicket.ticketId, obj).then((result) => {
        if (result.status == "done") {
          this.$toast.success("Працівників призначено");
          this.refreshTicketData();
        } else {
          this.$toast.error("Помилка при призначенні працівників");
        }
      });
    },
    handleRemoveAssignedUser(worker) {
      if (!this.activeTicket || !worker) {
        return;
      }

      // Створюємо новий список працівників без видаленого
      const remainingWorkers = this.activeTicket.assignedWorkers
        .filter((w) => w.workerId !== worker.workerId)
        .map((w) => w.workerId);

      const obj = {
        workers: remainingWorkers,
      };

      apiServe.addTeammates(this.activeTicket.ticketId, obj).then((result) => {
        if (result.status == "done") {
          this.$toast.success("Працівника видалено");
          this.refreshTicketData();
        } else {
          this.$toast.error("Помилка при видаленні працівника");
        }
      });
    },
    refreshTicketData() {
      if (!this.activeTicket) {
        return;
      }

      apiServe.getTicket(this.activeTicket.ticketId).then((result) => {
        if (result.status === "done" && result.data) {
          this.ticketWindowsManager.updateTicketData(
            this.windowId,
            this.activeTabIndex,
            result.data
          );
        }
      });
    },
  },
  mounted() {
    // Event bus listeners can be added here if needed
  },
  beforeUnmount() {
    // Cleanup if needed
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
}

.ticket-tabs-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  margin: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.main-content-area {
  display: flex;
  flex: 1;
  overflow: hidden;
  align-items: stretch;
}

.center-content {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-width: 0;
}

.content-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
}

.content-tabs {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.content-tabs :deep(.nav-tabs) {
  border-bottom: 1px solid #e9ecef;
  padding: 0 24px;
  background: white;
  flex-shrink: 0;
}

.content-tabs :deep(.nav-link) {
  border: none;
  color: #6c757d;
  padding: 12px 16px;
  font-size: 13px;
}

.content-tabs :deep(.nav-link.active) {
  color: #495057;
  border-bottom: 2px solid #1abc9c;
  background: transparent;
}

.content-tabs :deep(.tab-content) {
  height: calc(100% - 49px);
  min-height: 0;
  position: absolute;
  bottom: 0;
  top: 50px;
  left: 0;
  right: 0;
}

.content-tabs :deep(.tab-pane) {
  display: none;
}

.content-tabs :deep(.tab-pane.active) {
  display: block;
  height: 100%;
}

.content-footer {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  background: white;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

@media (max-width: 1200px) {
  .ticket-tabs-container {
    margin: 10px;
  }
}

@media (max-width: 992px) {
  .main-content-area {
    flex-direction: column;
  }

  .center-content {
    flex: 1;
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .ticket-tabs-container {
    margin: 5px;
    border-radius: 0;
  }

  .content-tabs :deep(.nav-tabs) {
    padding: 0 16px;
  }

  .content-tabs :deep(.nav-link) {
    padding: 10px 12px;
    font-size: 12px;
  }

  .content-footer {
    padding: 12px 16px;
  }
}

@media (max-width: 576px) {
  .ticket-tabs-container {
    margin: 0;
  }

  .main-content-area {
    flex-direction: column;
  }
}

@media (max-width: 768px) and (orientation: landscape) {
  .main-content-area {
    flex-direction: row;
  }

  .center-content {
    order: 2;
  }
}
</style>
